import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { EditorState } from 'draft-js';
import { htmlFromEditorState, editorStateFromHtml } from "app/utils/DraftConvert";

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import General from 'app/components/event/settings/General';
import Description from 'app/components/event/settings/Description';
import Invitations from 'app/components/event/settings/Invitations';
import Tickets from 'app/components/event/settings/Tickets';
import Review from 'app/components/event/settings/Review';

const steps = [
    'Details',
    'Full Description',
    'Invitations',
    'Tickets',
    'Review'
];

const useStyles = makeStyles(theme => ({
    input: {
        display: 'none',
    },
    listSubHeader: {
        backgroundColor: '#ffffff',
        color: theme.palette.error.main
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogPaper: {
        overflowY: 'initial'
    },
    height: {
        height: '100%'
    },
    content: {
        height: '100%',
        padding: '0'
    }
}));

const initEvent = {
    name: '',
    description: "",
    short_description: '',
    path: '',
    add_date_to_name: 0, // 0 = no, 1 = beginning of title, 2 = end of title
    location: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    invitation_title: '',
    public: true,
    rsvp: true,
    invitations_active: false,
    force_guest_ticket: false,
    group_tickets_required: false,
    guest_tickets_required: false,
    flag_outstanding: false,
    dates_allowed: 0,
    guests_allowed: 0,
    repeated: false,
    repeat_period: '',
    repeat_cycles: 0,
    repeat_amount: 0,
    add_date_to_name: 0,
    advanced_days_viewable: 30,
    advanced_days_rsvpable: 30,
    advanced_days_invitable: 30,
    advanced_days_purchasable: 30,
    starts_at: null,
    ends_at: null,
    published_at: '',
    updated_at: '',
    tickets: [],
    addSpouse: false,
    emailInvitation: false,
    display_snailmail: false
}


export default function NewEventDialog(props) {

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const history = useHistory();

    const initStart = DateTime.local().setZone(app.club.timezone).plus({days: 1}).startOf('hour');

    const { open, onClose, onClosed, copy } = props;

    const [ event, setEvent ] = useState({...initEvent, starts_at: initStart});
    const [ tickets, setTickets ] = useState([]);

    const [ groupsChecked, setGroupsChecked ] = useState([]);
    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ activeKey, setActiveKey ] = useState(0);
    const [ activeStep, setActiveStep ] = useState(0);
    const [ completedSteps, setCompletedSteps ] = useState({'-1': true});
    const [ advancedOpen, setAdvancedOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const [ editorState, setEditorState ] = useState(EditorState.createEmpty());

    // Getters ---------------------------------------------------

    const getGroups = () => api.get('/admin/group/group').then(res => setGroups(res.groups));

    const getLevels = () => api.get('/admin/group/level/recursive/0').then(res => setLevels(res.groups));

    useEffect(() => {
        
        setEvent({ ...event, ends_at: event.starts_at.plus({hours: 1}) });

    }, [event.starts_at]);

    useEffect(() => {
        getGroups();
        getLevels()

        if (copy) {
            setEvent(copy);
        }
    },  []);

    useEffect(() => {
        if (event.description) {
            setEditorState(editorStateFromHtml(event.description));
        }

        if (event.tickets) {
            setTickets(event.tickets);
        }
    }, [event]);

    // Handlers ---------------------------------------------------

    const handleEvent = (name, e) => {

        let value;

        if (DateTime.isDateTime(e) || !(typeof e === 'object')) {
            value = e;
        } else {
            const target = e.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setEvent({ ...event, [name]: value });
    };

    const handleLocation = ac => {

        if (isError('location')) delete errors.location;
        if (isError('address')) delete errors.address;
        if (isError('city')) delete errors.city;
        if (isError('state')) delete errors.state;
        if (isError('zip')) delete errors.zip;
        if (isError('country')) delete errors.country;

        setEvent({
            ...event,
            location: ac.name,
            address: ac.address,
            city: ac.city,
            state: ac.state,
            zip: ac.zip,
            country: ac.country
        });
    }

    const handleTickets = (name, event) => {

        if (isError(name)) delete errors[name];

        let value;
        const newTickets = [...tickets]

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        newTickets.splice(activeKey, 1, {...tickets[activeKey], [name]: value})

        if (newTickets[activeKey].type === 2) {newTickets[activeKey].type = null}

        setTickets(newTickets)

    };

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...groupsChecked]

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setGroupsChecked(newChecked);
    }

    const handleNext = () => {
        const nextStep = activeStep + 1;
        if (nextStep > 6) {
            postNewEvent();
        }
        else {
            const newCompleted = completedSteps;
            newCompleted[activeStep] = true;
            setCompletedSteps(newCompleted);
            setActiveStep(nextStep);
        }
    }

    const handleBack = () => {
        const previousStep = activeStep - 1;
        if (previousStep < 0) {
            handleClose()
        }
        else {
            const newCompleted = completedSteps;
            newCompleted[activeStep] = false;
            setCompletedSteps(newCompleted);
            setActiveStep(previousStep);
        }
    }

    // Posters ---------------------------------------------------

    const postEvent = (publish) => {

        setLoading(true)

        const data = {...event}

        data.tickets = [...tickets];
        data.groups = [...groupsChecked];
        data.description = htmlFromEditorState(editorState);

        if (! publish) data.published_at = null;

        if (event.starts_at) data.starts_at = event.starts_at.toSeconds();
        if (event.ends_at) data.ends_at = event.ends_at.toSeconds();

        api.post('/admin/event', data)
            .then(res => history.push(`/admin/events/${res.id}`))
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const handleClose = () => {
        onClose();
    }

    const handleClosed = () => {
        setEvent({
            ...initEvent,
            starts_at: initStart
        });
        setGroupsChecked([]);
        setTickets([]);
        setActiveStep(0);
        setActiveKey(0);

        if (onClosed) onClosed();
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const hasErrors = label => {
        let pagedFields = [];

        switch (label) {
            case 'Details':
                pagedFields = ['name', 'short_description', 'path', 'add_date_to_name', 'location', 'address', 'city', 'state', 'zip', 'country'];
                break;
            case 'Full Description':
                pagedFields = ['description'];
                break;
            case 'Invitations':
                pagedFields = ['invitation_title', 'public', 'rsvp', 'invitations_active', 'force_guest_ticket', 'group_tickets_required', 'guest_tickets_required', 'flag_outstanding', 'dates_allowed', 'guests_allowed'];
            case 'Tickets':
                pagedFields = ['tickets'];
                break;
        }

        const errorFields = pagedFields.filter(field => errors[field]);

        return errorFields.length;
    }

    const memoizedSteps = useMemo(() => {
        return (
            <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                    const labelProps = {};

                    if (hasErrors(label) > 0) {
                        labelProps.error = true;
                    }

                    return (
                        <Step key={index}>
                            <StepButton
                                onClick={() => setActiveStep(index)}
                                completed={completedSteps[index]}
                                disabled={! completedSteps[index - 1]}
                                >
                                <StepLabel {...labelProps}>
                                    {label}
                                </StepLabel>
                            </StepButton>
                        </Step>
                    )
                })}
            </Stepper>
        )
    }, [activeStep, errors]);

    const getDateFormat = () => {
        switch (event.repeat_period) {
            case 'years':
                return 'y';
            case 'months':
                return 'LLLL y';
            case 'weeks':
            case 'days':
                return 'DDD';
            default: return '';
        }
    };

    const eventNameWithDate = useMemo(() => {
        if (event.repeated) {

            const dateFormat = getDateFormat();

            switch (event.add_date_to_name) {
                case 0: return event.name;
                case 1:
                    return event.starts_at.toFormat(dateFormat) + ' ' + event.name;
                case 2:
                    return event.name + ' ' + event.starts_at.toFormat(dateFormat);
            }
        } else {
            return event.name;
        }

    }, [event.add_date_to_name, event.repeated, event.name, event.starts_at, event.repeat_period]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            onExited={handleClosed}
            fullScreen
            aria-labelledby="new-event"
            classes={{paperScrollPaper: classes.dialogPaper}}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="event-builder">
                <span>Event Builder</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {memoizedSteps}
            <DialogContent dividers className={classes.content}>
                { (activeStep === 0) &&
                    <General
                        event={event}
                        handleEvent={handleEvent}
                        handleLocation={handleLocation}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        eventNameWithDate={eventNameWithDate}
                        newEvent
                    />
                }
                { (activeStep === 1) &&
                    <Description
                        event={event}
                        handleEvent={handleEvent}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        newEvent
                        editorState={editorState}
                        setEditorState={setEditorState}
                    />
                }
                { (activeStep === 2) &&
                    <Invitations
                        event={event}
                        groups={groups}
                        levels={levels}
                        groupsChecked={groupsChecked}
                        handleGroup={handleGroup}
                        handleLevelChildren={handleLevelChildren}
                        handleEvent={handleEvent}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        loading={loading}
                        advancedOpen={advancedOpen}
                        setAdvancedOpen={setAdvancedOpen}
                        newEvent
                    />
                }
                { (activeStep === 3) &&
                    <Tickets
                        handleTickets={handleTickets}
                        tickets={tickets}
                        setTickets={setTickets}
                        activeKey={activeKey}
                        setActiveKey={setActiveKey}
                        newEvent
                    />
                }
                { (activeStep === 4) &&
                    <Review
                        event={event}
                        tickets={tickets}
                        groups={groups}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        eventNameWithDate={eventNameWithDate}
                        advancedOpen={advancedOpen}
                    />
                }
            </DialogContent>
            <DialogActions>
                <Grid container direction="row" justify="flex-end" spacing={1}>
                    <Grid item>
                        <Button onClick={handleBack}>
                            {activeStep === 0 ? 'Cancel' : 'Back'}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            onClick={() => postEvent(false)}
                            variant="outlined"
                            color="primary"
                            >
                            Save As Draft
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            onClick={activeStep === 4 ? () => postEvent(true) : handleNext}
                            variant="contained"
                            color='primary'
                            >
                            {activeStep === 4 ? 'Publish Now' : 'Next'}
                        </Button>
                    </Grid>
                </Grid>
           </DialogActions>
        </Dialog>
    )
}
